import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useGlobalStore } from '@/state/global.state';
import { useWorkflowStore } from '@/state/workflow.state';
import { useNodeDuplication } from '@/hooks/useNodeDuplication';
import {
  FF_DUPLICATE_AND_PRESERVE_CONNECTIONS,
  FF_SHIFT_DELETE_DELETE_AND_KEEP_SELECTION,
} from '@/consts/featureFlags';
import { I18N_KEYS } from '@/language/keys';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useFlowView } from '../Recipe/FlowContext';
import type { DuplicateNodeOptions } from '@/components/Recipe/flowTypes';
import type { NodeId } from 'web';
import type { DialogData } from '@/state/global.state';

interface NodeActions {
  deleteNode: (nodeId: NodeId) => void;
  deleteNodes: (nodeIds: NodeId[], skipDialog?: boolean) => void;
  deleteNodesPreservingBridgingConnections: (nodeIds: NodeId[], skipDialog?: boolean) => void;
  deleteSelected: () => void;
  duplicateNode: (nodeId: NodeId, options?: DuplicateNodeOptions) => void;
  duplicateNodes: (nodeIds: NodeId[], options?: DuplicateNodeOptions) => void;
  toggleLockNode: (nodeId: NodeId) => void;
  openOverlayDialog: (dialogData: DialogData) => void;
  closeOverlayDialog: () => void;
  setCoverImage: (poster: string) => Promise<void>;
}

export function useNodeActions(): NodeActions {
  const flowView = useFlowView();
  const isShiftDeleteBridgeEnabled = useFeatureFlagEnabled(FF_SHIFT_DELETE_DELETE_AND_KEEP_SELECTION);
  const isDuplicatePreserveInputsEnabled = useFeatureFlagEnabled(FF_DUPLICATE_AND_PRESERVE_CONNECTIONS);
  const { positionDuplicatedNodes, scrollNodeIntoViewIfNeeded } = useNodeDuplication();
  const { t } = useTranslation();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const openOverlayDialog = useGlobalStore((s) => s.openDialog);
  const closeOverlayDialog = useGlobalStore((s) => s.closeDialog);
  const { getItem: getAllowDeleteGroupWithoutWarning } = useLocalStorage<boolean>('allowDeleteGroupWithoutWarning');

  const deleteNodes = useCallback(
    (ids: NodeId[], skipDialog: boolean = false) => {
      if (!skipDialog && !getAllowDeleteGroupWithoutWarning()) {
        const groupIds = ids.filter((id) => flowView.isGroupNode(id));

        if (groupIds.length > 0) {
          openOverlayDialog({
            type: 'deleteGroup',
            allNodeIdsToDelete: ids,
          });
          return;
        }
      }

      const result = flowView.deleteNodes(ids);

      if (result.preventedGroups && result.preventedGroups.length > 0) {
        updateSnackbarData({
          text: t(I18N_KEYS.RECIPE_MAIN.FLOW.CANNOT_DELETE_GROUP_WITH_LOCKED_NODES),
          severity: 'warning',
          icon: null,
          isOpen: true,
        });
      }
    },
    [flowView, t, updateSnackbarData, openOverlayDialog, getAllowDeleteGroupWithoutWarning],
  );

  const deleteNodesPreservingBridgingConnections = useCallback(
    (ids: NodeId[], skipDialog: boolean = false) => {
      if (!isShiftDeleteBridgeEnabled) {
        deleteNodes(ids, skipDialog);
        return;
      }

      if (!skipDialog && !getAllowDeleteGroupWithoutWarning()) {
        const groupIds = ids.filter((id) => flowView.isGroupNode(id));

        if (groupIds.length > 0) {
          openOverlayDialog({
            type: 'deleteGroup',
            allNodeIdsToDelete: ids,
            preserveBridgingConnections: true,
          });
          return;
        }
      }

      const result = flowView.deleteNodes(ids, { preserveBridgingConnections: true });

      if (result.preventedGroups && result.preventedGroups.length > 0) {
        updateSnackbarData({
          text: t(I18N_KEYS.RECIPE_MAIN.FLOW.CANNOT_DELETE_GROUP_WITH_LOCKED_NODES),
          severity: 'warning',
          icon: null,
          isOpen: true,
        });
      }
    },
    [
      deleteNodes,
      flowView,
      getAllowDeleteGroupWithoutWarning,
      isShiftDeleteBridgeEnabled,
      openOverlayDialog,
      t,
      updateSnackbarData,
    ],
  );

  const deleteNode = useCallback((id: NodeId) => deleteNodes([id]), [deleteNodes]);

  const deleteSelected = useCallback(() => {
    if (flowView.selectedNodes.length > 0) {
      deleteNodes(flowView.selectedNodes);
    } else if (flowView.selectedEdges.length > 0) {
      flowView.deleteEdges(flowView.selectedEdges);
    }
  }, [flowView, deleteNodes]);

  const duplicateNode = useCallback(
    (id: NodeId, options?: DuplicateNodeOptions) => {
      const effective = options?.preserveExternalInputs && !isDuplicatePreserveInputsEnabled ? undefined : options;
      flowView.duplicateNodes([id], positionDuplicatedNodes, scrollNodeIntoViewIfNeeded, effective);
    },
    [flowView, positionDuplicatedNodes, scrollNodeIntoViewIfNeeded, isDuplicatePreserveInputsEnabled],
  );
  const duplicateNodes = useCallback(
    (nodeIds: NodeId[], options?: DuplicateNodeOptions) => {
      const effective = options?.preserveExternalInputs && !isDuplicatePreserveInputsEnabled ? undefined : options;
      flowView.duplicateNodes(nodeIds, positionDuplicatedNodes, scrollNodeIntoViewIfNeeded, effective);
    },
    [flowView, positionDuplicatedNodes, scrollNodeIntoViewIfNeeded, isDuplicatePreserveInputsEnabled],
  );
  const toggleLockNode = useCallback((id: NodeId) => flowView.toggleLockNode(id), [flowView]);
  const setCoverImage = useWorkflowStore((s) => s.setRecipePoster);

  return {
    deleteNode,
    deleteNodes,
    deleteNodesPreservingBridgingConnections,
    deleteSelected,
    duplicateNode,
    duplicateNodes,
    toggleLockNode,
    openOverlayDialog,
    closeOverlayDialog,
    setCoverImage,
  };
}
